/**
 * PeriodModeSelector — Perioden- und Modus-Auswahl der UStVA-Vorschau (M8b).
 *
 * Props-getrieben (kein State/Firebase): wählt Granularität (Monat/Quartal/Jahr) +
 * konkrete Periode und zeigt die aktive Versteuerungsart (Soll/Ist) read-only an, mit
 * Link zum Profil. §18-Rhythmus wird rein informativ als Tooltip/Hinweis erwähnt (keine
 * automatische Umschaltung, §5.5).
 *
 * @module PeriodModeSelector
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconInfo } from '../../icons';

import { SummaryPeriodKind } from '../../utils/expenseSummary';
import { TaxationMode } from '../../utils/vatReturn';

export interface PeriodModeSelectorProps {
  kind: SummaryPeriodKind;
  year: number;
  /** 1..4 (nur relevant bei kind==='quarter'). */
  quarter: number;
  /** 1..12 (nur relevant bei kind==='month'). */
  month: number;
  /** Konfigurierte Versteuerungsart (read-only-Anzeige). */
  taxationMode: TaxationMode;
  /** Ist-Pfad (M8c) aktiv? Steuert den Modus-Hinweis. */
  istEnabled: boolean;
  onKindChange: (kind: SummaryPeriodKind) => void;
  onYearChange: (year: number) => void;
  onQuarterChange: (quarter: number) => void;
  onMonthChange: (month: number) => void;
}

const SELECT_CLASS =
  'bg-surface-raised border border-strong text-primary text-sm rounded px-2 py-1.5 focus:ring-indigo-500 focus:border-indigo-500';

/** Lokalisierte Monatsnamen für die Monatsauswahl. */
function monthName(month: number, locale: string): string {
  // Tag 1 vermeidet Zeitzonen-/Monatsrollen-Effekte.
  return new Date(2020, month - 1, 1).toLocaleDateString(locale, { month: 'long' });
}

const PeriodModeSelector: React.FC<PeriodModeSelectorProps> = ({
  kind,
  year,
  quarter,
  month,
  taxationMode,
  istEnabled,
  onKindChange,
  onYearChange,
  onQuarterChange,
  onMonthChange,
}) => {
  const { t, i18n } = useTranslation('ustva');
  const locale = i18n.language || 'de';

  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= currentYear - 5; y -= 1) years.push(y);

  return (
    <div className="rounded-lg border border-hairline bg-surface-raised p-4 space-y-4">
      {/* Perioden-Auswahl */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="ustva-period-kind" className="block text-xs text-muted mb-1">
            {t('period.label')}
          </label>
          <select
            id="ustva-period-kind"
            value={kind}
            onChange={(e) => onKindChange(e.target.value as SummaryPeriodKind)}
            className={SELECT_CLASS}
          >
            <option value="month">{t('period.month')}</option>
            <option value="quarter">{t('period.quarter')}</option>
            <option value="year">{t('period.year')}</option>
          </select>
        </div>

        {kind === 'quarter' && (
          <div>
            <label htmlFor="ustva-period-quarter" className="block text-xs text-muted mb-1">
              {t('period.quarter')}
            </label>
            <select
              id="ustva-period-quarter"
              value={quarter}
              onChange={(e) => onQuarterChange(Number.parseInt(e.target.value, 10))}
              className={SELECT_CLASS}
            >
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  {t('period.quarterShort', { quarter: q })}
                </option>
              ))}
            </select>
          </div>
        )}

        {kind === 'month' && (
          <div>
            <label htmlFor="ustva-period-month" className="block text-xs text-muted mb-1">
              {t('period.month')}
            </label>
            <select
              id="ustva-period-month"
              value={month}
              onChange={(e) => onMonthChange(Number.parseInt(e.target.value, 10))}
              className={SELECT_CLASS}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {monthName(m, locale)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="ustva-period-year" className="block text-xs text-muted mb-1">
            {t('period.year')}
          </label>
          <select
            id="ustva-period-year"
            value={year}
            onChange={(e) => onYearChange(Number.parseInt(e.target.value, 10))}
            className={SELECT_CLASS}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Versteuerungs-Modus (read-only) + Profil-Link */}
      <div className="flex flex-wrap items-center gap-2 text-sm border-t border-hairline pt-3">
        <span className="text-muted">{t('mode.label')}:</span>
        <span className="font-medium text-accent-ink">
          {taxationMode === 'ist' ? t('mode.ist') : t('mode.soll')}
        </span>
        <Link to="/dashboard/konto" className="text-accent-ink hover:text-accent-ink underline">
          {t('mode.changeInProfile')}
        </Link>
        {taxationMode === 'ist' && !istEnabled && (
          <span className="text-[var(--tone-caution-fg)] text-xs">({t('mode.istUnavailableShort')})</span>
        )}
      </div>

      {/* §18-Rhythmus-Hinweis (rein informativ, §5.5) */}
      <p className="flex items-start gap-2 text-xs text-faint">
        <span className="mt-0.5">
          <IconInfo size={12} />
        </span>
        <span>{t('rhythmHint')}</span>
      </p>
    </div>
  );
};

export default PeriodModeSelector;
